// MorningTweak.jsx — the "Tweak" branch of Morning Mode.
//
// Reached when yesterday was rated 👎 (the verdict is already captured upstream
// via BrewRating → coffee_check_ins). Branch-gated: the recipe stays HIDDEN until
// the user says what was off, so the adjustment is grounded in feedback.
//
// Flow: descriptor chips (bitter/flat/strong/not-sweet/stomach + an "Other" follow-up note)
// → "Adjust recipe →" calls the server AI endpoint (generateBrewRecipe, source:
// "tweak"), which is deterministic-first and degrades gracefully. On a TRANSPORT
// failure we fall back CLIENT-side to store.computeBrew + suggestAdjustment so the
// branch is never a dead end. The adjusted recipe card (matching MorningCoffee)
// renders with a prominent one-line rationale; "Started brew" runs the same
// log → schedule-rating-push → complete transaction as the other branches.
//
// Josh's standing bias (docs/coffee-handoff.md): every adjustment leans SMOOTHER,
// SWEETER, LOWER-EXTRACTION, easier on the stomach — NEVER stronger. The client
// fallback honors grind/temp moves but drops any "stronger" ratio move, mirroring
// the server's applyDescriptors.

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@clerk/clerk-react";
import { GlassCard, PrimaryButton, SecondaryButton, Chip, Segmented, SpecTiles, Eyebrow } from "./pronto";
import { AidenProfileBanner } from "./AidenProfileBanner";
import { RecipeNotes } from "./RecipeNotes";
import { generateBrewRecipe, recipeToBrew } from "../lib/morning-ai";
import { suggestAdjustment, netTempDeltaC, nudgeGrindSetting, applyProfileOverrides, TASTE_DESCRIPTORS as DESCRIPTORS } from "../lib/coffee.js";
import { scheduleRatingPush } from "../lib/scheduled-push";
import { supabase } from "../lib/supabase";

// Same descriptor set/labels as BrewRating (shared via coffee.js), for
// consistency across touchpoints. The ids double as keywords: suggestAdjustment
// does substring matching on them.
const MODES = [{ id: "hot", label: "Hot" }, { id: "cold", label: "Cold brew" }];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const clampTemp = (t) => clamp(Math.round(t), 50, 99); // Aiden per-pulse temp bounds
const fToC = (f) => Math.round(((f - 32) * 5) / 9);

// Pre-select chips from whatever the down-rating left on the brew log — the check-in
// taste array, an explicit descriptors field, or keywords in the feedback/notes text.
function extractDescriptors(brewLog) {
  if (!brewLog) return [];
  const ids = new Set(DESCRIPTORS.map((d) => d.id));
  const found = new Set();
  const arr = brewLog.descriptors || brewLog.taste || brewLog.brew_data?.descriptors || [];
  (Array.isArray(arr) ? arr : []).forEach((v) => { if (typeof v === "string" && ids.has(v)) found.add(v); });
  const text = `${brewLog.feedback || ""} ${brewLog.notes || ""}`.toLowerCase();
  DESCRIPTORS.forEach((d) => { if (text.includes(d.id)) found.add(d.id); });
  return [...found];
}

// Deterministic client-side fallback (transport failure only — the server path is
// already deterministic-first). Mirrors api/brew-recipe.js applyDescriptors: honor
// grind/temp moves from suggestAdjustment, DROP any "stronger" ratio move, and
// guarantee a sane, gentler recipe even when nothing matched. Returns the same
// card shape the server returns, so rendering + recipeToBrew stay uniform.
function fallbackCard(base, descriptors, note, cups, mode, grinder = null) {
  const feedback = [...(descriptors || []), note || ""].filter(Boolean).join(" ").toLowerCase();
  const adjustments = suggestAdjustment(base, feedback, grinder);

  let grindSetting = base.grind?.setting;
  let ratioDelta = 0;
  let grindReason = null; // last grind rule wins the value, so it owns the reason
  const otherReasons = [];
  for (const a of adjustments) {
    if (a.lever === "grind" && Number.isFinite(a.newSetting)) { grindSetting = a.newSetting; grindReason = a.reason; }
    else if (a.lever === "temp") { otherReasons.push(a.reason); }
    else if (a.lever === "ratio" && a.direction === "weaker") { ratioDelta += a.deltaRatio || 0; otherReasons.push(a.reason); }
    // Any other ratio direction is dropped — never stronger.
  }
  const reasons = [...(grindReason ? [grindReason] : []), ...otherReasons];
  // Handoff-scale temperature: rules carry ±1°C deltas, net clamped to ±1°C.
  const tempDeltaC = netTempDeltaC(adjustments);

  // Guardrail: if nothing moved (a note that matched no rule) still produce a
  // sane, direction-correct nudge — on the caller's own grinder scale.
  if (grindSetting === base.grind?.setting && tempDeltaC === 0 && ratioDelta === 0) {
    const finer = /weak|thin|flat|watery/.test(feedback);
    const nudged = nudgeGrindSetting(base.grind?.setting, finer ? -0.2 : 0.3, grinder);
    if (nudged != null && nudged !== base.grind?.setting) {
      grindSetting = nudged;
      reasons.unshift(finer
        ? "Ground a touch finer to pull more sweetness — never more coffee."
        : "Eased extraction slightly coarser for a smoother, gentler cup.");
    }
  }

  // Apply temp/ratio through coffee.js so the profile stays consistent;
  // preserve any per-pulse ramp shape. Cold brew keeps its 176°F bloom floor.
  let out = base;
  if (tempDeltaC || ratioDelta) {
    const p = base.aidenProfile || {};
    const overrides = {};
    if (tempDeltaC) {
      overrides.ssPulseTemperatures = (p.ssPulseTemperatures || []).map((t) => clampTemp(t + tempDeltaC));
      overrides.batchPulseTemperatures = (p.batchPulseTemperatures || []).map((t) => clampTemp(t + tempDeltaC));
      if (p.bloomEnabled) {
        let bloomC = clampTemp(p.bloomTemperature + tempDeltaC);
        if (mode === "cold") bloomC = Math.max(fToC(176), bloomC);
        overrides.bloomTemperature = bloomC;
      }
    }
    if (ratioDelta) {
      overrides.ratio = +clamp((p.ratio || base.ratio) + ratioDelta, 10, 22).toFixed(1);
    }
    out = applyProfileOverrides(base, overrides);
  }
  // nudgeGrindSetting already snapped + clamped on the caller's own scale —
  // re-clamping to the Ode's 1–11 here would corrupt other grinders' settings.
  const setting = grindSetting;
  out = { ...out, grind: { ...out.grind, setting } };

  // One-line rationale, prominent on the card (e.g. "Ground to 5.7 (coarser) —
  // bitter/overextracted, coarser grind extracts less.").
  const bits = [];
  if (setting !== base.grind?.setting) bits.push(`Ground to ${setting} (${setting < base.grind.setting ? "finer" : "coarser"})`);
  if (tempDeltaC) bits.push(`temp ${tempDeltaC > 0 ? "up" : "down"} ${Math.abs(tempDeltaC)}°C`);
  if (ratioDelta) bits.push(`ratio eased to 1:${out.ratio}`);
  const rationale = (bits.length ? `${bits.join(", ")} — ` : "") + (reasons[0] || "Biased smoother on your taste profile.");

  return brewToCard(out, { cups, rationale, source: "tweak" });
}

// Denormalize a coffee.js brew into the /api/brew-recipe card shape (so both the
// AI path and the fallback path render identically and feed recipeToBrew cleanly).
function brewToCard(brew, { cups, rationale, source = "tweak" }) {
  const p = brew.aidenProfile || {};
  return {
    dose_g: brew.dose_g,
    cups: Number(cups),
    grind_setting: brew.grind?.setting,
    aiden_profile: p,
    params: {
      temp_f: brew.temp_f,
      temp_c: brew.temp_c,
      ratio: brew.ratio,
      bloom_s: brew.bloom_time_s,
      bloom_temp_f: p.bloomEnabled ? Math.round((p.bloomTemperature * 9) / 5 + 32) : null,
    },
    rationale,
    source,
  };
}

// When embedded inside the MorningCoffee one-card frame, render bare (the frame
// already supplies the glass card + padding); otherwise wrap in our own card.
function Card({ embedded, children }) {
  return embedded ? <div className="space-y-0">{children}</div> : <GlassCard className="p-5">{children}</GlassCard>;
}

export function MorningTweak({ store, session, resolved, onComplete, embedded = false }) {
  const { getToken } = useAuth();
  const bean = resolved?.activeBean || null;
  const cups = resolved?.cups || 8;

  const [mode, setMode] = useState("hot");
  const [selected, setSelected] = useState(() => extractDescriptors(resolved?.yesterdayBrew));
  const [other, setOther] = useState(false);     // "Other → follow-up" toggle
  const [note, setNote] = useState("");
  const [recipe, setRecipe] = useState(null);    // null → recipe hidden (branch gate)
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((t) => t !== id) : [...p, id]));

  // Enough to act on: at least one chip, or an "Other" note.
  const canAdjust = selected.length > 0 || (other && note.trim().length > 0);

  const tempF = recipe?.params?.temp_f;
  const ratio = recipe?.params?.ratio;
  const bloomS = recipe?.params?.bloom_s;

  if (!bean) {
    return (
      <GlassCard className="p-5 text-center">
        <div className="text-[13px] text-pronto-text-2">No open bag to tweak. Add a bag in Coffee to start brewing.</div>
      </GlassCard>
    );
  }

  // Chips → AI (deterministic-first, server) → client fallback on transport failure.
  const adjust = async () => {
    if (!canAdjust || busy) return;
    setBusy(true);
    const descriptors = other && note.trim() ? [...selected, note.trim()] : selected;
    try {
      const card = await generateBrewRecipe({
        getToken,
        bean,
        tasteProfile: store.tasteProfile,
        mode,
        cups,
        descriptors,
        source: "tweak",
        grinder: store.gear?.grinder,
      });
      setRecipe(card);
    } catch (e) {
      // Transport failure only — never dead-end. Recompute locally + adjust.
      console.warn("brew-recipe transport failed, using client fallback", e?.message);
      const base = store.computeBrew({ beanId: bean.id, cups, mode });
      setRecipe(base ? fallbackCard(base, selected, note, cups, mode, store.gear?.grinder) : null);
    } finally {
      setBusy(false);
    }
  };

  // "Started brew" — the same transaction the other branches run.
  const startBrew = async () => {
    if (!recipe || busy) return;
    setBusy(true);
    try {
      const brew = recipeToBrew(recipe, bean);
      const log = await store.logBrew({ ...brew, source: "tweak" });
      try {
        await scheduleRatingPush({
          supabase, userId: store.userId, brewLogId: log.id, beanName: bean.name,
          fireAt: new Date(Date.now() + 40 * 60 * 1000),
        });
      } catch (e) { console.warn("rating push schedule failed", e?.message); }
      await store.completeMorning(log.id);
      onComplete?.(log);
    } finally {
      setBusy(false);
    }
  };

  // Changing the brew mode invalidates a generated recipe — back to the gate.
  const changeMode = (m) => { setMode(m); setRecipe(null); };

  const adjustedBrew = recipe ? recipeToBrew(recipe, bean) : null;

  return (
    <Card embedded={embedded}>
      {/* Hot / Cold */}
      <Segmented variant="pill" options={MODES} value={mode} onChange={changeMode} className="mb-4" />

      {!recipe ? (
        // ── Gate: what was off? (recipe hidden until this is answered) ──
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          <Eyebrow className="mb-2">What was off about the {bean.name}?</Eyebrow>
          <div className="flex flex-wrap gap-2 mb-3">
            {DESCRIPTORS.map((d) => (
              <Chip key={d.id} tone={selected.includes(d.id) ? "accent" : "rest"} onClick={() => toggle(d.id)}>
                {d.label}
              </Chip>
            ))}
            <Chip tone={other ? "accent" : "rest"} onClick={() => setOther((v) => !v)}>Other…</Chip>
          </div>

          {other && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tell me more — muddy, astringent, thin…"
                className="w-full mb-3 px-3 py-2.5 rounded-[12px] bg-white/70 border border-pronto-text-4/30 text-[14px] text-pronto-ink placeholder:text-pronto-text-4 outline-none focus:border-pronto-navy/40"
              />
            </motion.div>
          )}

          <div className="text-[11.5px] text-pronto-text-3 mb-3">
            I'll bias the fix smoother and lower-extraction — never stronger.
          </div>

          <PrimaryButton onClick={adjust} disabled={!canAdjust || busy} className="w-full">
            {busy ? "Adjusting…" : "Adjust recipe →"}
          </PrimaryButton>
        </motion.div>
      ) : (
        // ── Adjusted recipe card (matches MorningCoffee) ──
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          {/* Beans */}
          <div className="mb-3">
            <div className="font-round font-bold text-[17px] text-pronto-ink leading-tight">{bean.name}</div>
            <div className="text-[12.5px] text-pronto-text-2">
              {[bean.roaster, bean.roast_level].filter(Boolean).join(" · ")}
            </div>
          </div>

          {/* Dose + grind */}
          <div className="flex items-end gap-5 mb-4">
            <div>
              <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
                {recipe.dose_g != null ? `${Math.round(recipe.dose_g)}g` : "—"}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">for {recipe.cups || cups} cups</div>
            </div>
            <div>
              <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
                {recipe.grind_setting ?? "—"}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">Ode grind</div>
            </div>
          </div>

          {(tempF || ratio || bloomS) && (
            <SpecTiles
              className="mb-4"
              items={[
                { label: "Temp", value: tempF ? `${Math.round(tempF)}°` : "—" },
                { label: "Ratio", value: ratio ? `1:${ratio}` : "—" },
                { label: "Bloom", value: bloomS ? `${bloomS}s` : "—" },
              ]}
            />
          )}

          {/* Rationale — the whole point of the Tweak branch, kept prominent */}
          {recipe.rationale && (
            <div className="mb-4 rounded-[13px] bg-[rgba(63,74,99,0.08)] px-3.5 py-2.5">
              <Eyebrow className="mb-1">Why this change</Eyebrow>
              <div className="text-[13.5px] text-pronto-ink leading-snug">{recipe.rationale}</div>
            </div>
          )}

          {/* 📝 Notes — expected flavor + tomorrow's pre-planned move */}
          <RecipeNotes beans={[bean]} brew={adjustedBrew} grinder={store.gear?.grinder} className="mb-3" />

          {/* Aiden profile banner — a tweak always writes a new/updated profile */}
          <AidenProfileBanner store={store} bean={bean} brew={adjustedBrew} branch="tweak" className="mb-3" />

          <button
            onClick={startBrew}
            disabled={busy}
            className="w-full h-[46px] rounded-[14px] font-sf text-[15px] font-semibold text-pronto-navy mb-2 disabled:opacity-60"
            style={{ background: "rgba(63,74,99,0.1)" }}
          >
            {busy ? "Logging…" : "Started the Aiden ✓"}
          </button>
          <SecondaryButton onClick={() => setRecipe(null)} disabled={busy} className="w-full">
            ← Change what was off
          </SecondaryButton>
        </motion.div>
      )}
    </Card>
  );
}
